import type { HttpPort, IAssistantService, Json } from "../contracts";
import type { SessionStore } from "./SessionStore";

// AssistantService — wraps /api/assistant/* lifecycle + SSE stream.
//
// Non-streaming routes go through HttpPort (same retry / refresh / 401 path
// as every other service). The streaming route uses raw `fetch()` + a
// ReadableStream because EventSource doesn't accept custom Authorization
// headers — we need the JWT on the request — and the message body is JSON
// (POST, not GET). Mirrors the legacy `assistantApi.ts:sendMessageStream`
// loop verbatim: data: <json>\n\n frames, partial-frame buffering, abort
// via AbortController.signal.

const BASE = "/api/assistant";

export class AssistantApiError extends Error {
    readonly status: number;
    constructor(message: string, status: number) {
        super(message);
        this.status = status;
        this.name = "AssistantApiError";
    }
}

interface ServerEnvelope {
    success?: boolean;
    status?: number;
    error?: string;
    [key: string]: unknown;
}

function unwrap<T extends ServerEnvelope>(env: T): T {
    if (env && env.success === false) {
        throw new AssistantApiError(
            env.error ?? "Request failed",
            typeof env.status === "number" ? env.status : 0,
        );
    }
    return env;
}

export class AssistantService implements IAssistantService {
    private readonly fetchFn: typeof fetch;
    constructor(
        private readonly http: HttpPort,
        private readonly session: SessionStore,
        private readonly baseUrl: string = "",
        fetchFn?: typeof fetch,
    ) {
        // fetch must be invoked with the window/globalThis as receiver —
        // assigning the global `fetch` as a class property and calling
        // `this.fetchFn(...)` triggers "Illegal invocation" because the
        // implicit `this` becomes the AssistantService instance. Bind to
        // globalThis up front; tests can still inject a plain function.
        this.fetchFn = fetchFn ?? globalThis.fetch.bind(globalThis);
    }

    async startSession(req: Json): Promise<Json> {
        const env = unwrap(await this.http.post<ServerEnvelope>(`${BASE}/session`, req));
        return env as unknown as Json;
    }

    async sendMessage(sessionId: string, req: Json): Promise<Json> {
        const env = unwrap(
            await this.http.post<ServerEnvelope>(
                `${BASE}/session/${encodeURIComponent(sessionId)}/message`,
                req,
            ),
        );
        return env as unknown as Json;
    }

    async sendMessageStream(
        sessionId: string,
        req: Json,
        onEvent: (event: Json) => void,
        signal?: AbortSignal,
    ): Promise<void> {
        const token = this.session.getAccessToken();
        if (!token) {
            throw new AssistantApiError("Not signed in.", 401);
        }

        const url = `${this.baseUrl}${BASE}/session/${encodeURIComponent(sessionId)}/message/stream`;
        const response = await this.fetchFn(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Accept: "text/event-stream",
            },
            body: JSON.stringify(req),
            signal,
        });

        if (!response.ok || response.body === null) {
            let text = "";
            try {
                text = await response.text();
            } catch {
                // ignore
            }
            throw new AssistantApiError(
                text || `Stream connect failed: HTTP ${response.status}`,
                response.status,
            );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // SSE frames are separated by \n\n. Each frame may have multiple
            // `data:` lines that concatenate per the SSE spec § 9.2.6.
            let sep = buffer.indexOf("\n\n");
            while (sep >= 0) {
                const block = buffer.slice(0, sep);
                buffer = buffer.slice(sep + 2);
                const dataLines: string[] = [];
                for (const line of block.split("\n")) {
                    if (line.startsWith("data: ")) {
                        dataLines.push(line.slice(6));
                    } else if (line.startsWith("data:")) {
                        dataLines.push(line.slice(5));
                    }
                }
                if (dataLines.length > 0) {
                    try {
                        onEvent(JSON.parse(dataLines.join("\n")) as Json);
                    } catch {
                        // Malformed payload — skip, keep the stream alive.
                    }
                }
                sep = buffer.indexOf("\n\n");
            }
        }
    }

    async notifyIntentApplied(sessionId: string, notice: Json): Promise<void> {
        // Best-effort — provenance log is non-critical; failures must NOT
        // block the UI or surface error toasts.
        try {
            await this.http.post(
                `${BASE}/session/${encodeURIComponent(sessionId)}/intent-applied`,
                notice,
            );
        } catch {
            // intentional
        }
    }

    async closeSession(sessionId: string): Promise<void> {
        await this.http.post(`${BASE}/session/${encodeURIComponent(sessionId)}/close`, {});
    }

    async listSessions(params: Json = {}): Promise<Json> {
        const p = (params ?? {}) as Record<string, unknown>;
        const qs = new URLSearchParams();
        if (typeof p.action_key === "string") qs.set("action_key", p.action_key);
        if (typeof p.include_archived === "boolean") {
            qs.set("include_archived", p.include_archived ? "true" : "false");
        }
        if (typeof p.starred_only === "boolean") {
            qs.set("starred_only", p.starred_only ? "true" : "false");
        }
        if (typeof p.limit === "number") qs.set("limit", String(p.limit));
        if (typeof p.cursor === "string") qs.set("cursor", p.cursor);
        const s = qs.toString();
        const env = unwrap(
            await this.http.get<ServerEnvelope>(s ? `${BASE}/sessions?${s}` : `${BASE}/sessions`),
        );
        return env as unknown as Json;
    }

    async archiveSession(sessionId: string): Promise<void> {
        unwrap(
            await this.http.post<ServerEnvelope>(
                `${BASE}/session/${encodeURIComponent(sessionId)}/archive`,
                {},
            ),
        );
    }

    async starSession(sessionId: string): Promise<boolean> {
        const env = unwrap(
            await this.http.post<ServerEnvelope>(
                `${BASE}/session/${encodeURIComponent(sessionId)}/star`,
                {},
            ),
        );
        return Boolean(env.is_starred);
    }

    async renameSession(sessionId: string, title: string): Promise<string> {
        const env = unwrap(
            await this.http.post<ServerEnvelope>(
                `${BASE}/session/${encodeURIComponent(sessionId)}/rename`,
                { title },
            ),
        );
        return String(env.title ?? "");
    }
}
